import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, KeyRound, LayoutDashboard, Loader2, MailCheck, ShieldCheck } from "lucide-react";
import PartnerAuthShell from "@/components/partners/PartnerAuthShell";
import { Button } from "@/components/ui/button";
import { getCsrfToken } from "@/integrations/php/client";

type ActivationContext = {
  partner_name: string;
  masked_email: string;
  has_credentials: boolean;
};

const inviteStorageKey = "partner-dashboard-invite";

const captureInvite = (): string => {
  const hash = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
  const token = new URLSearchParams(hash).get("invite") ?? "";
  const valid = /^v1\.[0-9]{10}\.[0-9a-f-]{36}\.[A-Za-z0-9_-]{43}$/i.test(token);
  if (valid) {
    sessionStorage.setItem(inviteStorageKey, token);
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    return token;
  }
  return sessionStorage.getItem(inviteStorageKey) ?? "";
};

const PartnerActivate = () => {
  const [searchParams] = useSearchParams();
  const source = ["stage-reminder", "lead-success"].includes(searchParams.get("src") ?? "")
    ? (searchParams.get("src") as string)
    : "stage-reminder";
  const [invite] = useState(captureInvite);
  const [context, setContext] = useState<ActivationContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!invite) {
      setError("Ο σύνδεσμος ενεργοποίησης δεν είναι διαθέσιμος.");
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const response = await fetch(`/api/partner-setup-context.php?source=${encodeURIComponent(source)}`, {
          credentials: "include",
          headers: { "X-Partner-Setup-Token": invite },
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body?.error?.message ?? "Ο σύνδεσμος δεν είναι διαθέσιμος.");
        setContext(body.data);
      } catch (reason) {
        sessionStorage.removeItem(inviteStorageKey);
        setError(reason instanceof Error ? reason.message : "Ο σύνδεσμος δεν είναι διαθέσιμος.");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [invite, source]);

  const requestAccess = async () => {
    setSending(true);
    setError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(`/api/partner-setup-invite-request.php?source=${encodeURIComponent(source)}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Partner-Setup-Token": invite,
        },
        body: "{}",
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.error?.message ?? "Η αποστολή δεν ολοκληρώθηκε.");
      setSent(true);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Η αποστολή δεν ολοκληρώθηκε.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PartnerAuthShell>
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
        {loading ? (
          <div className="flex min-h-72 flex-col items-center justify-center text-slate-600">
            <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
            <p className="mt-3 text-sm">Έλεγχος ασφαλούς πρόσβασης...</p>
          </div>
        ) : sent ? (
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700"><MailCheck className="h-8 w-8" /></div>
            <h1 className="mt-5 text-2xl font-bold tracking-tight text-slate-950">Ελέγξτε το email σας</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">Στείλαμε τον προσωπικό σύνδεσμο ενεργοποίησης στο <strong>{context?.masked_email}</strong>. Ισχύει για 30 λεπτά και χρησιμοποιείται μία φορά.</p>
            <Button asChild className="mt-7 h-12 w-full rounded-xl bg-sky-600 font-bold hover:bg-sky-700"><Link to="/partner/login">Μετάβαση στη σύνδεση</Link></Button>
          </div>
        ) : context ? (
          <>
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-50 text-sky-700"><LayoutDashboard className="h-7 w-7" /></div>
            <p className="mt-5 text-xs font-bold uppercase tracking-[0.18em] text-sky-700">Dashboard συνεργάτη</p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-950">{context.partner_name}</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">Δείτε όλες τις επαφές, τα στάδια, τις ενέργειες και τα στατιστικά σας σε ένα απλό περιβάλλον, φτιαγμένο και για κινητό.</p>
            <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="flex items-start gap-2 text-sm font-semibold leading-6 text-emerald-900"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" /> Για επιβεβαίωση, ο προσωπικός σύνδεσμος θα σταλεί στο {context.masked_email}. Δεν χρειάζεται να πληκτρολογήσετε email.</p>
            </div>
            {error && <p role="alert" className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>}
            <Button type="button" onClick={() => void requestAccess()} disabled={sending} className="mt-6 h-12 w-full rounded-xl bg-sky-600 text-base font-bold hover:bg-sky-700">
              {sending ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <KeyRound className="mr-2 h-5 w-5" />}
              {context.has_credentials ? "Στείλτε νέο ασφαλή σύνδεσμο" : "Ενεργοποίηση του dashboard μου"}
            </Button>
            <p className="mt-4 text-center text-xs leading-5 text-slate-500">Η προβολή μεμονωμένων επαφών από email ή SMS συνεχίζει να λειτουργεί χωρίς σύνδεση.</p>
          </>
        ) : (
          <div className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-700"><ShieldCheck className="h-7 w-7" /></div>
            <h1 className="mt-4 text-2xl font-bold text-slate-950">Ο σύνδεσμος δεν είναι διαθέσιμος</h1>
            <p className="mt-2 text-sm leading-6 text-slate-600">{error || "Ζητήστε νέο σύνδεσμο ενεργοποίησης από τη σελίδα σύνδεσης."}</p>
            <Button asChild variant="outline" className="mt-6 h-12 w-full rounded-xl"><Link to="/partner/password-reset">Δημιουργία ή επαναφορά πρόσβασης</Link></Button>
          </div>
        )}
        {!loading && !sent && context && <p className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Ασφαλής ενεργοποίηση μέσω καταχωρημένου email</p>}
      </div>
    </PartnerAuthShell>
  );
};

export default PartnerActivate;
